import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { connectEmail, getEmailSettings, disconnectEmail, syncEmails } from '../../lib/api';
import api from '../../lib/api';
import EmailAccountsManager from '../EmailAccountsManager';
import { Settings, Mail, Lock, Eye, EyeOff, Save, CheckCircle, XCircle, RefreshCw, Loader2, Building, Server, Globe, MessageSquare, Phone, Bell } from 'lucide-react';

function SettingsPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [autoCheck, setAutoCheck] = useState(true);
  const [checkInterval, setCheckInterval] = useState('5');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  
  // Server settings
  const [imapServer, setImapServer] = useState('imap.hostinger.com');
  const [smtpServer, setSmtpServer] = useState('smtp.hostinger.com');
  const [imapPort, setImapPort] = useState('993');
  const [smtpPort, setSmtpPort] = useState('465');
  const [useSSL, setUseSSL] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Slack settings
  const [slackWebhook, setSlackWebhook] = useState('');
  const [slackNotifyEmails, setSlackNotifyEmails] = useState(true);
  const [slackNotifyApprovals, setSlackNotifyApprovals] = useState(true);
  const [slackConnected, setSlackConnected] = useState(false);
  const [slackTesting, setSlackTesting] = useState(false);

  // WhatsApp settings
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappNotifyEmails, setWhatsappNotifyEmails] = useState(true);
  const [whatsappNotifyApprovals, setWhatsappNotifyApprovals] = useState(true);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [whatsappTesting, setWhatsappTesting] = useState(false);

  useEffect(function() {
    loadSettings();
    loadNotificationSettings();
  }, []);

  async function loadNotificationSettings() {
    try {
      // Load Slack settings
      const slackRes = await api.get('/notifications/slack/settings');
      if (slackRes.data.slack_webhook_url) {
        setSlackWebhook(slackRes.data.slack_webhook_url);
        setSlackConnected(true);
        setSlackNotifyEmails(slackRes.data.notify_new_emails !== false);
        setSlackNotifyApprovals(slackRes.data.notify_approvals !== false);
      }

      // Load WhatsApp settings
      const whatsappRes = await api.get('/notifications/whatsapp/settings');
      if (whatsappRes.data.whatsapp_number) {
        setWhatsappNumber(whatsappRes.data.whatsapp_number);
        setWhatsappConnected(true);
        setWhatsappNotifyEmails(whatsappRes.data.whatsapp_notify_emails !== false);
        setWhatsappNotifyApprovals(whatsappRes.data.whatsapp_notify_approvals !== false);
      }
    } catch (e) {
      console.error('Error loading notification settings:', e);
    }
  }

  async function loadSettings() {
    try {
      const response = await getEmailSettings();
      const settings = response.data;
      if (settings.email_address) {
        setEmail(settings.email_address);
        setIsConnected(settings.is_connected || false);
        setAutoCheck(settings.auto_check !== false);
        setCheckInterval(String(settings.check_interval || 5));
        setLastSync(settings.last_sync);
        setImapServer(settings.imap_server || 'imap.hostinger.com');
        setSmtpServer(settings.smtp_server || 'smtp.hostinger.com');
        setImapPort(String(settings.imap_port || 993));
        setSmtpPort(String(settings.smtp_port || 465));
        setUseSSL(settings.use_ssl !== false);
      }
    } catch (e) {
      console.error('Error loading settings:', e);
    }
  }

  async function handleTestConnection() {
    if (!email || !password) {
      toast.error('يرجى إدخال البريد وكلمة المرور');
      return;
    }
    setTesting(true);
    try {
      const response = await connectEmail({
        email_address: email,
        password: password,
        imap_server: imapServer,
        smtp_server: smtpServer,
        imap_port: parseInt(imapPort),
        smtp_port: parseInt(smtpPort),
        use_ssl: useSSL,
        auto_check: autoCheck,
        check_interval: parseInt(checkInterval)
      });
      
      if (response.data.success) {
        setIsConnected(true);
        setPassword(''); // Clear password after successful connection
        toast.success('تم الاتصال بنجاح! Nicole يمكنها الآن قراءة وإرسال الإيميلات');
      }
    } catch (e) {
      setIsConnected(false);
      const errorMsg = e.response?.data?.detail || 'فشل الاتصال - تحقق من البيانات';
      toast.error(errorMsg);
    } finally {
      setTesting(false);
    }
  }

  async function handleDisconnect() {
    try {
      await disconnectEmail();
      setIsConnected(false);
      setPassword('');
      setLastSync(null);
      toast.success('تم قطع الاتصال');
    } catch (e) {
      toast.error('فشل قطع الاتصال');
    }
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const response = await syncEmails();
      const data = response.data;
      toast.success(`تم مزامنة ${data.new_emails} رسائل جديدة`);
      setLastSync(new Date().toISOString());
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'فشل المزامنة';
      toast.error(errorMsg);
    } finally {
      setSyncing(false);
    }
  }

  // Slack handlers
  async function handleTestSlack() {
    if (!slackWebhook) {
      toast.error('يرجى إدخال Webhook URL');
      return;
    }
    setSlackTesting(true);
    try {
      const response = await api.post('/notifications/slack/test', null, {
        params: { webhook_url: slackWebhook }
      });
      if (response.data.success) {
        toast.success('تم إرسال رسالة اختبار إلى Slack بنجاح!');
      }
    } catch (e) {
      toast.error('فشل الاتصال بـ Slack - تحقق من Webhook URL');
    } finally {
      setSlackTesting(false);
    }
  }

  async function handleSaveSlack() {
    if (!slackWebhook) {
      toast.error('يرجى إدخال Webhook URL');
      return;
    }
    try {
      await api.post('/notifications/slack/configure', null, {
        params: {
          webhook_url: slackWebhook,
          notify_new_emails: slackNotifyEmails,
          notify_approvals: slackNotifyApprovals,
          notify_quotes: true
        }
      });
      setSlackConnected(true);
      toast.success('تم حفظ إعدادات Slack بنجاح');
    } catch (e) {
      toast.error('فشل حفظ الإعدادات');
    }
  }

  // WhatsApp handlers
  async function handleTestWhatsApp() {
    if (!whatsappNumber) {
      toast.error('يرجى إدخال رقم WhatsApp');
      return;
    }
    setWhatsappTesting(true);
    try {
      const response = await api.post('/notifications/whatsapp/test', null, {
        params: { phone_number: whatsappNumber }
      });
      if (response.data.success) {
        toast.success('تم إرسال رسالة اختبار إلى WhatsApp بنجاح!');
      }
    } catch (e) {
      toast.error('فشل إرسال رسالة WhatsApp - تحقق من الرقم');
    } finally {
      setWhatsappTesting(false);
    }
  }

  async function handleSaveWhatsApp() {
    if (!whatsappNumber) {
      toast.error('يرجى إدخال رقم WhatsApp');
      return;
    }
    try {
      await api.post('/notifications/whatsapp/configure', null, {
        params: {
          phone_number: whatsappNumber,
          notify_new_emails: whatsappNotifyEmails,
          notify_approvals: whatsappNotifyApprovals,
          notify_quotes: true
        }
      });
      setWhatsappConnected(true);
      toast.success('تم حفظ إعدادات WhatsApp بنجاح');
    } catch (e) {
      toast.error('فشل حفظ الإعدادات');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
          <Settings className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">الإعدادات</h1>
          <p className="text-muted-foreground mt-1">Settings - إعدادات البريد والإشعارات</p>
        </div>
      </div>

      <Tabs defaultValue="email" className="space-y-6">
        <TabsList className="grid grid-cols-3 w-full max-w-lg">
          <TabsTrigger value="email" className="flex items-center gap-2" data-testid="email-tab">
            <Mail className="h-4 w-4" />
            البريد
          </TabsTrigger>
          <TabsTrigger value="slack" className="flex items-center gap-2" data-testid="slack-tab">
            <MessageSquare className="h-4 w-4" />
            Slack
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="flex items-center gap-2" data-testid="whatsapp-tab">
            <Phone className="h-4 w-4" />
            WhatsApp
          </TabsTrigger>
        </TabsList>

        {/* Email Settings Tab */}
        <TabsContent value="email" className="space-y-6">
          {/* Multiple Email Accounts Manager */}
          <EmailAccountsManager />
          
          {/* Nicole Email Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />حساب Nicole
              </CardTitle>
              <CardDescription>معلومات حساب البريد الخاص بـ Nicole</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="font-bold text-primary">N</span>
                  </div>
                  <div>
                    <p className="font-medium">nicole@dhr-sa.com</p>
                    <p className="text-sm text-muted-foreground">Nicole AI Business Assistant</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  جميع الرسائل المرسلة من Nicole ستكون من هذا الحساب، مع وضع باقي الموظفين في CC
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Slack Settings Tab */}
        <TabsContent value="slack" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      إشعارات Slack
                      {slackConnected ? (
                        <Badge className="bg-green-500/10 text-green-500 border-green-500/20">متصل</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">غير متصل</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>استقبل إشعارات Nicole على قناة Slack</CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />Webhook URL
                </Label>
                <Input
                  value={slackWebhook}
                  onChange={function(e) { setSlackWebhook(e.target.value); }}
                  placeholder="https://hooks.slack.com/services/..."
                  className="bg-input/50 font-mono text-sm"
                  data-testid="slack-webhook-input"
                />
                <p className="text-xs text-muted-foreground">
                  أنشئ Webhook من إعدادات Slack: Apps → Incoming Webhooks
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Bell className="h-4 w-4" />أنواع الإشعارات
                </h4>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                  <div>
                    <Label className="text-base">رسائل البريد الجديدة</Label>
                    <p className="text-sm text-muted-foreground">إشعار عند وصول رسالة جديدة</p>
                  </div>
                  <Switch checked={slackNotifyEmails} onCheckedChange={setSlackNotifyEmails} data-testid="slack-notify-emails" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                  <div>
                    <Label className="text-base">طلبات الموافقة</Label>
                    <p className="text-sm text-muted-foreground">إشعار عند وجود طلب موافقة جديد</p>
                  </div>
                  <Switch checked={slackNotifyApprovals} onCheckedChange={setSlackNotifyApprovals} data-testid="slack-notify-approvals" />
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={handleTestSlack} disabled={slackTesting || !slackWebhook} data-testid="test-slack-btn">
                  {slackTesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageSquare className="h-4 w-4 mr-2" />}
                  {slackTesting ? 'جاري الاختبار...' : 'اختبار الاتصال'}
                </Button>
                <Button onClick={handleSaveSlack} disabled={!slackWebhook} data-testid="save-slack-btn">
                  <Save className="h-4 w-4 mr-2" />
                  حفظ الإعدادات
                </Button>
              </div>

              <div className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
                <h4 className="font-medium text-sm text-purple-500 mb-2">كيفية إنشاء Webhook URL</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>افتح إعدادات Slack workspace</li>
                  <li>اذهب إلى Apps → Manage → Custom Integrations</li>
                  <li>اختر Incoming Webhooks</li>
                  <li>انقر على "Add to Slack" واختر القناة</li>
                  <li>انسخ Webhook URL والصقه هنا</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Settings Tab */}
        <TabsContent value="whatsapp" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      إشعارات WhatsApp
                      {whatsappConnected ? (
                        <Badge className="bg-green-500/10 text-green-500 border-green-500/20">متصل</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">غير متصل</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>استقبل إشعارات Nicole على WhatsApp</CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />رقم WhatsApp
                </Label>
                <Input
                  value={whatsappNumber}
                  onChange={function(e) { setWhatsappNumber(e.target.value); }}
                  placeholder="+966XXXXXXXXX"
                  className="bg-input/50 font-mono"
                  dir="ltr"
                  data-testid="whatsapp-number-input"
                />
                <p className="text-xs text-muted-foreground">
                  أدخل الرقم مع رمز الدولة (مثل: +966552168158)
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Bell className="h-4 w-4" />أنواع الإشعارات
                </h4>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                  <div>
                    <Label className="text-base">رسائل البريد الجديدة</Label>
                    <p className="text-sm text-muted-foreground">إشعار WhatsApp عند وصول رسالة</p>
                  </div>
                  <Switch checked={whatsappNotifyEmails} onCheckedChange={setWhatsappNotifyEmails} data-testid="whatsapp-notify-emails" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                  <div>
                    <Label className="text-base">طلبات الموافقة</Label>
                    <p className="text-sm text-muted-foreground">إشعار عند وجود طلب موافقة جديد</p>
                  </div>
                  <Switch checked={whatsappNotifyApprovals} onCheckedChange={setWhatsappNotifyApprovals} data-testid="whatsapp-notify-approvals" />
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={handleTestWhatsApp} disabled={whatsappTesting || !whatsappNumber} data-testid="test-whatsapp-btn">
                  {whatsappTesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Phone className="h-4 w-4 mr-2" />}
                  {whatsappTesting ? 'جاري الإرسال...' : 'اختبار الإرسال'}
                </Button>
                <Button onClick={handleSaveWhatsApp} disabled={!whatsappNumber} data-testid="save-whatsapp-btn">
                  <Save className="h-4 w-4 mr-2" />
                  حفظ الإعدادات
                </Button>
              </div>

              <div className="p-4 rounded-lg bg-green-500/5 border border-green-500/20">
                <h4 className="font-medium text-sm text-green-500 mb-2">ملاحظات WhatsApp</h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>تأكد من أن الرقم مسجل على WhatsApp</li>
                  <li>الإشعارات تُرسل عبر Twilio WhatsApp API</li>
                  <li>قد تحتاج لإرسال رسالة "join" أولاً إلى رقم Twilio</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default SettingsPage;
